import { ChangeDetectionStrategy, Component, computed, effect, inject, input, linkedSignal, output, untracked } from '@angular/core';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { NoContentStubComponent } from '@frontendlib/components/components/no-content-stub/no-content-stub.component';
import { Pagination2Component } from '@modules/common/components/pagination/pagination2.component';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { BoardListStore } from '../../../services/board-list.store';
import { BoardsStore } from '../../../services/boards.store';
import { toBoardFolderRows } from '../../shared/board-folder-rows.util';
import { BoardsFolderCardComponent } from '../boards-folder-card/boards-folder-card.component';

/** Every live folder, as cards, for the Boards page's "Folders" view (the sidebar's "Folders" label). Opening
 * one is the host's to do. */
@Component({
  selector: 'app-boards-folders-list',
  templateUrl: './boards-folders-list.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex min-h-0 flex-1 flex-col' },
  imports: [Button2Component, LoadingIndicatorComponent, NoContentStubComponent, Pagination2Component, BoardsFolderCardComponent],
})
export class BoardsFoldersListComponent {
  /** Lower-cased title filter; the dynamic panel's folder search (Slides' `folderSearch`). */
  public readonly search = input('');
  public readonly openFolder = output<{ id: string; selectAll: boolean }>();
  public readonly boardCreated = output<{ folderId: string; boardId: string }>();

  protected readonly store = inject(BoardsStore);
  private readonly listStore = inject(BoardListStore);
  private readonly spaceContextStore = inject(SpaceContextStore);

  protected readonly pageSize = 25;
  protected readonly rows = computed(() =>
    toBoardFolderRows(this.store.sortedFolders(), this.store.boards(), this.listStore.view(), this.search().trim().toLocaleLowerCase(), this.spaceContextStore.contextId() ?? ''));
  /** Count updates keep the page; a view or folder-sort change starts again from page 1. */
  protected readonly page = linkedSignal({
    source: () => ({ view: this.listStore.view(), search: this.search().trim().toLocaleLowerCase(), options: this.store.folderOptions() }),
    computation: (): number => 1,
  });
  protected readonly pagedRows = computed(() => this.rows().slice((this.page() - 1) * this.pageSize, this.page() * this.pageSize));

  constructor() {
    // A page that emptied out (its last folders archived or trashed) falls back to the last page with rows.
    effect(() => {
      const lastPage = Math.max(1, Math.ceil(this.rows().length / this.pageSize));
      if (untracked(() => this.page()) > lastPage) this.page.set(lastPage);
    });
  }
}
